/**
 * Viewport fix for iOS WKWebView
 *
 * The native shell can inject its own <meta name="viewport"> tag after the
 * HTML loads, overriding the one in index.html and losing viewport-fit=cover.
 * This runs at bundle-load time (before React mounts) and watches for any
 * subsequent overrides so the settings are always enforced.
 */
export function applyViewportFix(): void {
  if (typeof document === 'undefined') return;

  // `interactive-widget` is a Chromium-only viewport key. WebKit (Safari, and
  // every browser on iOS) rejects it and logs "Viewport argument key
  // 'interactive-widget' not recognized and ignored" every time the meta is
  // parsed. Gate it to Chromium engines so Safari never sees it — Android
  // Chrome still gets the keyboard-overlay behaviour. navigator.userAgentData
  // is a reliable Chromium marker (absent in Safari/Firefox and on iOS).
  const supportsInteractiveWidget =
    typeof navigator !== 'undefined' && 'userAgentData' in navigator;
  const interactiveWidget = supportsInteractiveWidget
    ? ', interactive-widget=overlays-content'
    : '';

  const fix = () => {
    const metas = document.querySelectorAll('meta[name="viewport"]');

    if (metas.length === 0) {
      const meta = document.createElement('meta');
      meta.name = 'viewport';
      meta.content =
        `width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover${interactiveWidget}`;
      document.head.appendChild(meta);
      return;
    }

    metas.forEach(meta => {
      const current = meta.getAttribute('content') || '';
      let updated = current;

      if (!updated.includes('viewport-fit=cover'))                    updated += ', viewport-fit=cover';
      if (!updated.includes('maximum-scale=1'))                       updated += ', maximum-scale=1';
      if (!updated.includes('user-scalable=no'))                      updated += ', user-scalable=no';
      if (supportsInteractiveWidget && !updated.includes('interactive-widget=overlays-content'))
        updated += ', interactive-widget=overlays-content';

      if (updated !== current) meta.setAttribute('content', updated);
    });
  };

  // Apply immediately
  fix();

  // Watch for the native shell re-injecting or mutating the viewport meta
  const observer = new MutationObserver(mutations => {
    const affected = mutations.some(m => {
      if (m.type === 'childList') {
        return Array.from(m.addedNodes).some(
          n => n.nodeName === 'META' && (n as HTMLMetaElement).name === 'viewport'
        );
      }
      return (
        m.type === 'attributes' &&
        m.target.nodeName === 'META' &&
        (m.target as HTMLMetaElement).name === 'viewport'
      );
    });
    if (affected) fix();
  });

  observer.observe(document.head, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['content', 'name'],
  });
}